import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateRange } from "react-day-picker";

import {
  StatusSummaryResponse,
  fetchStatusSummary,
  getTotalAmount,
  getTotalCount,
  getTotalPriceWithIva,
} from "~/services/statusSummaryService";
import { useProfileStore } from "~/store/profileStore";

interface UseStatusSummaryProps {
  dateRange?: DateRange;
  viewAllSellers?: boolean;
}

export const useStatusSummary = ({
  dateRange,
  viewAllSellers = false,
}: UseStatusSummaryProps) => {
  const [statusSummary, setStatusSummary] = useState<StatusSummaryResponse>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add initialization guard to prevent infinite re-fetches
  const isInitialized = useRef(false);

  const { id: userId, role: userRole } = useProfileStore();

  // Serialize dateRange for stable dependency comparison
  // Using getTime() to compare primitive values instead of object references
  const serializedDateRange = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    return `${dateRange.from.toISOString()}_${dateRange.to.toISOString()}`;
  }, [dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  const fetchSummary = useCallback(async (forceRefetch = false) => {
    // Prevent re-fetches after initialization unless explicitly requested
    if (!forceRefetch && isInitialized.current) {
      return;
    }

    // Don't fetch if no valid date range
    if (!dateRange?.from || !dateRange?.to) {
      setStatusSummary({});
      return;
    }

    setIsLoading(true);
    setError(null);
    isInitialized.current = true;

    try {
      const data = await fetchStatusSummary({
        dateRange,
        userRole,
        userId,
        viewAllSellers,
      });

      setStatusSummary(data);
    } catch (err) {
      console.error("Error fetching status summary:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch status summary"
      );
      setStatusSummary({});
    } finally {
      setIsLoading(false);
    }
  }, [serializedDateRange, userRole, userId, viewAllSellers]);

  // Reset initialization guard when dependencies change so we can refetch
  useEffect(() => {
    isInitialized.current = false;
  }, [serializedDateRange, userRole, userId, viewAllSellers]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Explicit refetch function that bypasses the guard
  const refetch = useCallback(() => {
    fetchSummary(true);
  }, [fetchSummary]);

  // Helper functions for totals
  const totalCount = getTotalCount(statusSummary);
  const totalAmount = getTotalAmount(statusSummary);
  const totalPriceWithIva = getTotalPriceWithIva(statusSummary);

  return {
    statusSummary,
    isLoading,
    error,
    totalCount,
    totalAmount,
    totalPriceWithIva,
    refetch,
  };
};
